import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Palette, Package, Layers, Grid, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import StokSekme from '../../components/StokSekme';
import StokTablosu from '../../components/StokTablosu';
import StokAktarModal from '../../components/StokAktarModal';
import { formatCurrency } from '../../components/StockIndicator';
import BoyaTable from './BoyaTable';
import BoyaModal from './BoyaModal';
import { boyaKategorileri, getBoyaByInsaat } from '@mocks/materials/boya';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import { getHareketler, hareketTipleri } from '@mocks/materials/stokHareketleri';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Boya() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const [activeTab, setActiveTab] = useState('kullanim');
    const [filterKategori, setFilterKategori] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isAktarOpen, setIsAktarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;

    const filteredData = useMemo(() => {
        let data = getBoyaByInsaat(insaatId);
        if (filterKategori) data = data.filter(i => i.kategori === filterKategori);
        return data;
    }, [insaatId, filterKategori]);

    const stats = useMemo(() => {
        const toplamKalem = filteredData.length;
        const stokKg = filteredData.reduce((acc, s) => acc + s.stokMiktar, 0);
        const toplamMetrekare = filteredData.reduce((acc, s) => acc + s.uygulamaMetrekare, 0);
        const toplamMaliyet = filteredData.reduce((acc, s) => acc + s.toplamFiyat, 0);
        return { toplamKalem, stokKg, toplamMetrekare, toplamMaliyet };
    }, [filteredData]);

    const stokHavuzuData = getStokHavuzu('boya');
    const hareketlerData = getHareketler('boya');

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };
    const handleSubmit = async (fd, isEdit) => {
        setIsLoading(true); await new Promise(r => setTimeout(r, 800));
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            addMockCariHareket(fd.supplierId, insaatId || 'c1', { type: 'purchase', subtype: 'fatura', amount: fd.kdvDahilToplam, date: fd.siparisTarihi || new Date().toISOString().split('T')[0], description: fd.autoDescription || `Boya — ${fd.miktar} ${fd.birim}` });
        }
        toast.success(isEdit ? 'Boya güncellendi' : 'Yeni boya eklendi');
        setIsLoading(false); setIsModalOpen(false);
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) { setOdemePlanData({ amount: fd.kdvDahilToplam, companyName: fd.tedarikci }); setIsOdemePlanOpen(true); }
    };
    const handleConfirmDelete = async () => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success('Boya silindi'); setIsLoading(false); setIsDeleteOpen(false); };
    const handleAktarim = async (d) => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success(`${d.miktar} lt stok aktarıldı`); setIsLoading(false); setIsAktarOpen(false); };
    const handleOdemePlanConfirm = ({ nextDueDate }) => { toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`); setIsOdemePlanOpen(false); setOdemePlanData(null); };

    const statsCards = [
        { title: 'Toplam Kalem', value: `${stats.toplamKalem}`, icon: Package, color: 'primary' },
        { title: 'Stok', value: `${stats.stokKg} kg`, icon: Layers, color: 'info' },
        { title: 'Boyanan Alan', value: `${stats.toplamMetrekare} m²`, icon: Grid, color: 'success' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'warning' },
    ];

    const detailFields = [{ key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' }, { key: 'kategori', label: 'Kategori' }, { key: 'urunAdi', label: 'Ürün Adı' }, { key: 'renk', label: 'Renk' }, { key: 'miktar', label: 'Miktar', type: 'number' }, { key: 'stokMiktar', label: 'Stok', type: 'number' }, { key: 'uygulamaMetrekare', label: 'Boyanan', type: 'number' }, { key: 'toplamFiyat', label: 'Tutar', type: 'currency' }, { key: 'durum', label: 'Durum', type: 'status' }];

    const headerFilter = (
        <div className="flex items-center gap-3">
            <StokSekme activeTab={activeTab} onTabChange={setActiveTab} />
            {activeTab === 'kullanim' && (
                <select value={filterKategori} onChange={(e) => setFilterKategori(e.target.value)} className="px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 min-w-[160px]">
                    <option value="">Tüm Kategoriler</option>
                    {boyaKategorileri.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
            )}
        </div>
    );

    return (
        <>
            <MaterialPageLayout title="Boya" subtitle="İnce İşler" icon={Palette} iconBgColor="bg-warning" backLink="/insaat/ince" stats={statsCards} onAddClick={activeTab === 'kullanim' ? handleAdd : undefined} headerFilter={headerFilter}>
                {activeTab === 'kullanim' ? (
                    <BoyaTable data={filteredData} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} onUpdateStock={() => { }} />
                ) : (
                    <StokTablosu stokData={stokHavuzuData} hareketData={hareketlerData} hareketTipleri={hareketTipleri} birim="lt" onAktarim={() => setIsAktarOpen(true)} />
                )}
            </MaterialPageLayout>
            <BoyaModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="Boya Detayı" />
            <StokAktarModal isOpen={isAktarOpen} onClose={() => setIsAktarOpen(false)} onSubmit={handleAktarim} malzemeTipi="boya" stokData={stokHavuzuData} birim="lt" isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleConfirmDelete} title="Boyayı Sil" message={`"${selectedItem?.urunAdi}" boyasını silmek istediğinize emin misiniz?`} isLoading={isLoading} />
            <OdemePlanMiniModal isOpen={isOdemePlanOpen} onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }} onConfirm={handleOdemePlanConfirm} amount={odemePlanData?.amount} companyName={odemePlanData?.companyName} />
        </>
    );
}
